import React from 'react';
import {PropTypes} from 'prop-types';
import {Link} from '../../shared/link';
import numeral from 'numeral';
import moment from 'moment-timezone';
import {MakeApiCall, WriteConsoleErrors, ReplaceUrl} from '../../shared/utils';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';
import {LoadingIndicatorComponent} from '../../shared/loadingIndicatorComponent';
import {RefreshButtonComponent} from '../../shared/refreshButtonComponent';
import {AccountComponent} from '../AccountsListComponent/account';
import {InfoItem} from '../AccountInfo/infoitem';
import {DateTimeItem} from '../AccountInfo/datetimeitem';
import {UrlBuilder, INVOICE_STATE} from '../../app.defs';
import {EditNotesDialogComponent} from '../../shared/EditNotesDialog';
import {PageHeader} from '../../shared/PageHeader';

import './styles.scss';

const INVOICEINFO_ACTION = {
    NONE: 0,
    LOADING: 1,
    EDITNOTES: 2,
}

export class InvoiceInfoComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            invoiceId: props.invoiceId,
            isLoaded: false,
            action: INVOICEINFO_ACTION.NONE,
            isLoading: false,
            errors: null,
            invoiceInfo: null,
            requestId: 0
        }

        this.doLoad = this.doLoad.bind(this);
        this.handleEditNotesDialogHide = this.handleEditNotesDialogHide.bind(this);
        this.doSaveNotes = this.doSaveNotes.bind(this);
        this.handleEditNotesClick = this.handleEditNotesClick.bind(this);
        this.goToInvoiceId = this.goToInvoiceId.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }
        const requestId = self.state.requestId + 1;

        const data = {InvoiceId: self.state.invoiceId, ListSource: self.props.listSource};

        new Promise(resolve => {
            self.setState({action: INVOICEINFO_ACTION.LOADING, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getInvoiceInfo',
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                );
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    action: INVOICEINFO_ACTION.NONE,
                    isLoaded: true,
                    invoiceInfo: response,
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({action: INVOICEINFO_ACTION.NONE, errors: errors});
            }
        });
    }

    goToInvoiceId(invoiceId) {
        const self = this;

        if (self._isMounted) {
            ReplaceUrl(UrlBuilder.InvoiceInfo({InvoiceId: invoiceId, ListSource: self.props.listSource}));
    
            self.setState({invoiceId: invoiceId}, self.doLoad);
        }
    }

    handleEditNotesClick() {
        this.setState({action: INVOICEINFO_ACTION.EDITNOTES});
    }

    doSaveNotes(notes) {
        const self = this;
        const data = {InvoiceId: self.state.invoiceId, Notes: notes};
        const verificationToken = self.state.invoiceInfo.VerificationToken;

        const result = MakeApiCall(
            $.post(
                {
                    url: '/api/admin/saveInvoiceNotes',
                    headers: {RequestVerificationToken: verificationToken},
                    data: JSON.stringify(data),
                    contentType: 'application/json',
                    dataType: 'json',
                    cache: false
                }
            )
        )
        .then(response => {
            if (self._isMounted) {
                const invoiceInfo = {...self.state.invoiceInfo, AdminNotes: notes};
                self.setState({invoiceInfo: invoiceInfo});
            }

            return response;
        });

        return result;
    }

    handleEditNotesDialogHide() {
        const self = this;

        if (self.state.action == INVOICEINFO_ACTION.EDITNOTES) {
            self.setState({action: INVOICEINFO_ACTION.NONE});
        }
    }

    render() {
        const self = this;
        const {invoiceId, invoiceInfo, action, isLoaded, errors} = self.state;
        const {timeZoneId, listSource} = self.props;

        const isDisabled = !isLoaded || (action != INVOICEINFO_ACTION.NONE);

        const utcNow = moment.utc();

        const buttonPrev = Boolean(invoiceInfo) &&
            <Link 
                key="buttonPrev"
                className="btn btn-outline-secondary btn-sm newstyle" 
                disabled={isDisabled || !Boolean(invoiceInfo.PrevId)} 
                to={() => self.goToInvoiceId(invoiceInfo.PrevId)}
            >
                Prev
            </Link>
        
        const buttonNext = Boolean(invoiceInfo) &&
            <Link 
                key="buttonNest"
                className="btn btn-outline-secondary btn-sm newstyle" 
                disabled={isDisabled || !Boolean(invoiceInfo.NextId)} 
                to={() => self.goToInvoiceId(invoiceInfo.NextId)}
            >
                Next
            </Link>

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={75}
                    caption={
                        <h2 className="text-truncate">                                    
                            <span className="mr-2">Invoice #{invoiceId}</span>
                            {Boolean(invoiceInfo) && !invoiceInfo.IsRefunded && (invoiceInfo.Id == invoiceId) && !invoiceInfo.IsHiddenForUser &&
                                <Link 
                                    to={UrlBuilder.InvoicePaper({InvoiceId: invoiceInfo.Id})} 
                                    target="_blank" 
                                    disabled={isDisabled}
                                    className="mr-2"
                                >
                                    <span className="fa fa-file-pdf-o" />
                                </Link>
                            }
                            <RefreshButtonComponent 
                                disabled={action == INVOICEINFO_ACTION.LOADING} 
                                animated={action == INVOICEINFO_ACTION.LOADING} 
                                onClick={self.doLoad} 
                                className="fs-085"
                            />
                        </h2>
                    }
                    buttons={[<div key="buttonGroup" className="btn-group">{[buttonPrev, buttonNext].filter(Boolean)}</div>]}
                />
                <ErrorsBlockComponent errors={errors} />
                {isLoaded && (action == INVOICEINFO_ACTION.EDITNOTES) &&
                    <EditNotesDialogComponent
                        title={<span className="fs-125"><span className="mr-1 fa fa-edit" />Notes for invoice #{invoiceInfo.Id}</span>}
                        notes={invoiceInfo.AdminNotes}
                        doSave={self.doSaveNotes}
                        onHide={self.handleEditNotesDialogHide}
                    />
                }
                {isLoaded &&
                    <div>
                        <InfoItem caption="Account" content={<AccountComponent id={invoiceInfo.AccountId} name={invoiceInfo.AccountName} disabled={isDisabled} showId showLoginAs linkToInfo />} />
                        <InfoItem caption="Invoice type" content={invoiceInfo.InvoiceTypeName} />
                        <InfoItem caption="Created" content={<DateTimeItem utcDate={invoiceInfo.CreatedAt} timeZoneId={timeZoneId} format="L LTS (z)" />} />
                        <InfoItem caption="State" content={<span className={INVOICE_STATE.textClass(invoiceInfo.StateId)}>{invoiceInfo.StateName}</span>} />
                        {!invoiceInfo.IsPaid && Boolean(invoiceInfo.PaymentDeadLine) && 
                            <InfoItem 
                                caption="Payment deadline" 
                                content={
                                    <DateTimeItem 
                                        utcDate={invoiceInfo.PaymentDeadLine} 
                                        timeZoneId={timeZoneId} 
                                        format="L LTS (z)" 
                                        className={utcNow.isAfter(invoiceInfo.PaymentDeadLine) > 0 ? 'text-danger' : 'text-warning'} 
                                    />
                                } 
                            />
                        }
                        {invoiceInfo.IsPaid && <InfoItem caption="Paid" content={<DateTimeItem utcDate={invoiceInfo.PaidAt} timeZoneId={timeZoneId} format="L LTS (z)" />} />}
                        {invoiceInfo.IsRefunded && <InfoItem caption="Paid" content={<DateTimeItem utcDate={invoiceInfo.RefundedAt} timeZoneId={timeZoneId} format="L LTS (z)" />} />}
                        {Boolean(invoiceInfo.AffiliateInfo) && 
                            <InfoItem 
                                caption="Affiliate"
                                content={
                                    <div className="text-nowrap">
                                        <Link to={invoiceInfo.AffiliateInfo.AdminUrl} target="_blank" className="mr-2">#{invoiceInfo.AffiliateInfo.Id}</Link>
                                        <span className="mr-2">{invoiceInfo.AffiliateInfo.Name}</span>
                                        <span>(commission: {numeral(invoiceInfo.AffiliateCommission).USD()})</span>
                                    </div>
                                }
                            />
                        }
                        <div className="pt-4">
                            <table className="invoicebody table-hover">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Service</th>
                                        <th className="text-right">Amount</th>
                                        <th className="text-right">Discount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceInfo.Items.map((p, i) => {
                                        return (
                                            <tr key={i}>
                                                <td className="text-right">
                                                    {i + 1}
                                                </td>
                                                <td>
                                                    {p.Name}
                                                    <div className="small text-secondary">{p.Description}</div>
                                                </td>
                                                <td className="text-right">
                                                    {(p.Quantity == 1) && numeral(p.Price).USD('0,0[.]0000')}
                                                    {(p.Quantity != 1) && `${numeral(p.Price).USD('0,0[.]0000')} x ${numeral(p.Quantity).format('0[.]0000')}`}
                                                </td>
                                                <td className="text-right">
                                                    {numeral(p.DiscountPercent).format('0[.]00')}%
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                                <tfoot>
                                    {(invoiceInfo.CalculatedAmount != invoiceInfo.Amount) && 
                                        <tr>
                                            <td />
                                            <td className="text-right">SubTotal</td>
                                            <td className="text-right">{numeral(invoiceInfo.CalculatedAmount).USD()}</td>
                                            <td />
                                        </tr>
                                    }
                                    {(invoiceInfo.CalculatedAmount > invoiceInfo.Amount) && 
                                        <tr>
                                            <td />
                                            <td className="text-right">Discount</td>
                                            <td className="text-right">-{numeral(invoiceInfo.CalculatedAmount - invoiceInfo.Amount).USD()}</td>
                                            <td />
                                        </tr>
                                    }
                                    <tr>
                                        <td />
                                        <td className="text-right">Total</td>
                                        <td className="text-right">{numeral(invoiceInfo.Amount).USD()}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        <InfoItem vertical caption="Notes" content={<textarea className="form-control compact text-body bg-white" rows={2} readOnly value={invoiceInfo.AdminNotes || ''} />} />
                   </div>
                }

                {isLoaded &&
                    <div className="pt-4">
                        <button 
                            type="button"
                            className="btn btn-outline-primary btn-sm newstyle"
                            disabled={isDisabled}
                            onClick={self.handleEditNotesClick}
                        >
                            <span className="mr-1 fa fa-edit" />Edit notes
                        </button>
                    </div>
                }
            </div>
        )
    }
}
InvoiceInfoComponent.propTypes = {
    invoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    timeZoneId: PropTypes.string.isRequired,
    listSource: PropTypes.string,
}